import * as React from 'react';
import { connect } from 'react-redux';

import Tooltip from 'components/Tooltip';
import MetricsBar from 'components/metrics-bar/MetricsBar';
import MoreButton from 'components/buttons/more-button/MoreButton';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Spinner from 'components/spinner/Spinner';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import { withModal } from 'components/modal/ModalHOC';

import SubcampaignsTableShortlistedCustomizationModal from '../subcampaigns-table-shortlisted-customization-modal/SubcampaignsTableShortlistedCustomizationModal';

import {
  PARTICIPANT_TYPE_AGREED, PARTICIPANT_TYPE_REVOKED,
  PARTICIPANT_TYPE_SHORTLISTED
} from 'utils/campaignParticipants';
import { __t, applyTranslations } from 'utils/language';

import type { ContextMenuItem, MetricsBarItem } from 'types';
import type { Dispatch } from 'redux';

import './subcampaigns-table-header.scss';

applyTranslations(require('./subcampaigns-table-header.i18n.json'));

type OwnProps = {
  campaignId: number,
  isEmpty: boolean,
  participantsType: string,
  metrics: Array<MetricsBarItem>,
  contextMenuItems?: Array<ContextMenuItem>,
  showSpinner?: boolean,
  onShowAddresses?: () => void,
  onShowAnalytics?: () => void
};

type StateProps = {
  campaignUISettingsLoaded: boolean
};

type Props = {
  // eslint-disable-next-line react/no-unused-prop-types
  dispatch: Dispatch // added by connect so we should keep it here
} & OwnProps & StateProps;

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom'
});

const TooltipWithModal = withModal(Tooltip, {
  modalContent: SubcampaignsTableShortlistedCustomizationModal,
  width: 'small'
});

const SubcampaignsTableHeader = (
  {
    campaignId,
    campaignUISettingsLoaded,
    isEmpty = false,
    contextMenuItems = [],
    metrics = [],
    participantsType,
    showSpinner,
    onShowAddresses,
    onShowAnalytics
  }: Props
): JSX.Element => {
  const shouldShowAddress = onShowAddresses && participantsType === PARTICIPANT_TYPE_AGREED;
  const shouldShowAnalytics = onShowAnalytics && participantsType !== PARTICIPANT_TYPE_REVOKED;
  const shouldShowCustomizationModal = participantsType === PARTICIPANT_TYPE_SHORTLISTED;

  const areMetricsAndTitleVisible = !isEmpty || participantsType === PARTICIPANT_TYPE_SHORTLISTED;

  const renderSpinner = (): React.ReactNode => (
    <div className="subcampaigns-table-header__spinner">
      <Spinner
        position="inline"
        size="tiny"
        color="light"
      />
    </div>
  );

  return (
    <section className="subcampaigns-table-header">
      {areMetricsAndTitleVisible && (
        <div className="subcampaigns-table-header__title">
          {__t(`subcampaigns_table_header.headline.${participantsType}`)}
        </div>
      )}
      <div className="subcampaigns-table-header__metrics">
        {areMetricsAndTitleVisible && (
          <MetricsBar
            metrics={metrics}
          />
        )}
      </div>
      {shouldShowAddress && (
        <div className="subcampaigns-table-header__action">
          <Tooltip message={__t('subcampaigns_table_header.tooltip.address')}>
            <SVGDynamicIcon
              name="address-book-custom-fg"
              disabled={isEmpty}
              customColor="grey"
              width={24}
              height={24}
              onClick={!isEmpty ? onShowAddresses : undefined}
            />
          </Tooltip>
        </div>
      )}
      {shouldShowAnalytics && (
        <div className="subcampaigns-table-header__action">
          <Tooltip message={__t('subcampaigns_table_header.tooltip.analytics')}>
            <SVGDynamicIcon
              name="equalizer-custom-bg"
              disabled={isEmpty}
              customColor="grey"
              width={24}
              height={24}
              onClick={!isEmpty ? onShowAnalytics : undefined}
            />
          </Tooltip>
        </div>
      )}
      {
        shouldShowCustomizationModal && (
          <div className="subcampaigns-table-header__action">
            <TooltipWithModal
              message={__t('subcampaigns_table_header.tooltip.customization')}
              key={campaignId}
              modalContentProps={{ campaignId }}
            >
              <SVGDynamicIcon
                width={24}
                height={24}
                name="gear-custom-bg"
                customColor="grey"
                clickable
                disabled={isEmpty || campaignUISettingsLoaded}
              />
            </TooltipWithModal>
          </div>
        )
      }
      {contextMenuItems.length > 0 && (
        <div className="subcampaigns-table-header__action subcampaigns-table-header__action-context-menu">
          <MoreButtonWithMenu
            disabled={isEmpty}
            width={24}
            iconWidth={5}
            iconHeight={24}
            contextMenuProps={{ items: contextMenuItems }}
          />
        </div>
      )}
      {showSpinner && renderSpinner()}
    </section>
  );
};

const mapStateToProps = ({ campaignUISettingsShorlisted }): StateProps => ({
  campaignUISettingsLoaded: campaignUISettingsShorlisted.campaignUISettingsLoaded
});

export default connect(mapStateToProps)(SubcampaignsTableHeader);
